import React, { createContext, useContext, useReducer, useEffect } from "react";
import axios from "axios";

// Initial state
const initialState = {
  user: null,
  tenant: null,
  token: localStorage.getItem("token"),
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const actionTypes = {
  SET_LOADING: "SET_LOADING",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  UPDATE_USER: "UPDATE_USER",
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case actionTypes.LOGIN_SUCCESS:
      const { user, tenant, token } = action.payload;
      localStorage.setItem("token", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      if (tenant) {
        localStorage.setItem("tenantId", tenant.id);
        axios.defaults.headers.common["X-Tenant-ID"] = tenant.id;
      }
      return {
        ...state,
        user,
        tenant,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case actionTypes.LOGIN_FAILURE:
      localStorage.removeItem("token");
      localStorage.removeItem("tenantId");
      delete axios.defaults.headers.common["Authorization"];
      delete axios.defaults.headers.common["X-Tenant-ID"];
      return {
        ...state,
        user: null,
        tenant: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case actionTypes.LOGOUT:
      localStorage.removeItem("token");
      localStorage.removeItem("tenantId");
      delete axios.defaults.headers.common["Authorization"];
      delete axios.defaults.headers.common["X-Tenant-ID"];
      return {
        ...initialState,
        isLoading: false,
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case actionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case actionTypes.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set up axios defaults
  useEffect(() => {
    if (state.token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${state.token}`;
    }
  }, [state.token]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      const tenantId = localStorage.getItem("tenantId");

      if (token) {
        try {
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          if (tenantId) {
            axios.defaults.headers.common["X-Tenant-ID"] = tenantId;
          }

          const response = await axios.get("/api/auth/me");

          dispatch({
            type: actionTypes.LOGIN_SUCCESS,
            payload: {
              user: response.data.user,
              tenant: response.data.tenant,
              token: token,
            },
          });
        } catch (error) {
          console.error("Auth check failed:", error);
          // Only logout if it's a 401/403 error, not network issues
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            localStorage.removeItem("token");
            localStorage.removeItem("tenantId");
            dispatch({ type: actionTypes.LOGOUT });
          } else {
            // For network errors, keep the user logged in but show loading as false
            dispatch({ type: actionTypes.SET_LOADING, payload: false });
          }
        }
      } else {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (email, password, tenantDomain) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: true });

    try {
      // Prepare login payload - only include tenantDomain if provided
      const loginPayload = {
        email,
        password,
      };

      // Only add tenantDomain if it's provided and not empty
      if (tenantDomain && tenantDomain.trim() !== "") {
        loginPayload.tenantDomain = tenantDomain;
      }

      const response = await axios.post("/api/auth/login", loginPayload);

      dispatch({
        type: actionTypes.LOGIN_SUCCESS,
        payload: response.data,
      });

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Login failed";
      dispatch({
        type: actionTypes.LOGIN_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: true });

    try {
      const response = await axios.post("/api/auth/register", userData);

      dispatch({
        type: actionTypes.LOGIN_SUCCESS,
        payload: response.data,
      });

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Registration failed";
      dispatch({
        type: actionTypes.SET_ERROR,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    dispatch({ type: actionTypes.LOGOUT });
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    try {
      await axios.post("/api/auth/forgot-password", { email });
      return { success: true, message: "Password reset email sent" };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Failed to send reset email";
      return { success: false, error: errorMessage };
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      const response = await axios.put("/api/auth/profile", userData);

      dispatch({
        type: actionTypes.UPDATE_USER,
        payload: response.data.user,
      });

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Profile update failed";
      return { success: false, error: errorMessage };
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: actionTypes.CLEAR_ERROR });
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    forgotPassword,
    updateProfile,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
